import { hashKey, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { PermissionKey } from '@/modules/api/openapi/models/PermissionKey'
import { UserUpdateRequest } from '@/modules/api/openapi/models/UserUpdateRequest'
import { internalApi } from '@/modules/api/request'
import { useAuth } from '@/modules/authentication'
import { getImpersonatedUserId } from '@/modules/authentication/impersonation'
import { monitorException } from '@/modules/monitoring'

/**
 * `/users/me` resolves against the authenticated account and the impersonation header, so
 * its cache entry has to be scoped to that identity. The query client is only cleared on
 * the sign-out and impersonation paths, and entries stay fresh for minutes, so an
 * unscoped key lets a response outlive the identity it was fetched for.
 */
export const buildCurrentUserQueryKey = (firebaseUid: string | undefined) => [
  '/users/me',
  firebaseUid,
  getImpersonatedUserId(),
]

export const useCurrentUser = () => {
  const { data: currentUser } = useAuth()
  return useSuspenseQuery({
    queryKey: buildCurrentUserQueryKey(currentUser?.uid),
    queryFn: async () => await internalApi.users.getCurrentUser(),
  })
}

export const buildGetCurrentUserPermissionsQueryOptions = () => ({
  queryKey: ['/users/me/permissions'],
  queryFn: async () => await internalApi.users.getCurrentUserPermissions(),
})

export const useCurrentPermissions = () => {
  const { loading } = useAuth()
  return useQuery({
    ...buildGetCurrentUserPermissionsQueryOptions(),
    enabled: !loading,
  })
}

export const usePermission = (permissionKey: PermissionKey) => {
  const { data: permissions } = useCurrentPermissions()
  const permission = permissions?.permissions.find(p => p.key === permissionKey)
  if (!permission) monitorException(`Permission doesn't exist: ${permissionKey}`)
  return !!permission?.enabled
}

export const usePatchCurrentUser = () => {
  const { data: currentUser } = useAuth()
  return useMutation({
    mutationFn: async (requestBody: UserUpdateRequest) =>
      await internalApi.users.updateUser({ requestBody }),
    // Snapshot the key before the request goes out: the identity can change while it is
    // in flight, and the response belongs to the identity that issued it.
    onMutate: () => ({ queryKey: buildCurrentUserQueryKey(currentUser?.uid) }),
    onSuccess: (data, _variables, { queryKey }, { client }) => {
      // Switching identity clears the cache, so a response that arrives afterwards must
      // not repopulate an entry for the identity that just went away.
      if (hashKey(queryKey) !== hashKey(buildCurrentUserQueryKey(currentUser?.uid))) return
      client.setQueryData(queryKey, data)
    },
  })
}
